import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Target, Hash, MapPin, Users, UserPlus, RefreshCw, Info, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { targetDataApi } from '../lib/api';
import { mergeTargetData, toTargetDataBody, serverTargetSettings, seedServerValues } from '../lib/targetData';
import { r14UiEnabled } from '../lib/r14Switch';
import NotFoundTargetsCard from '../components/NotFoundTargetsCard';


// Target types
const TARGET_TYPES = [
  { value: 'HASHTAGS', label: 'Hashtags', icon: Hash, description: 'Target users from hashtag pages' },
  { value: 'LOCATIONS', label: 'Locations', icon: MapPin, description: 'Target users from location pages' },
  { value: 'COMPETITORACCOUNTS', label: 'Competitor Accounts', icon: Users, description: 'Target followers of competitor accounts' },
  { value: 'DIRECTACCOUNTS', label: 'Direct Accounts', icon: UserPlus, description: 'Direct list of usernames to target' },
];

// Selection types
const SELECTION_TYPES = [
  { value: 'POSTER', label: 'Poster', description: 'Target the person who posted' },
  { value: 'POSTENGAGER', label: 'Post Engager', description: 'Target users who engaged with posts (likers/commenters)' },
  { value: 'FOLLOWINGS', label: 'Followings', description: 'Target from followings list' },
  { value: 'FOLLOWERS', label: 'Followers', description: 'Target from followers list' },
];

// Top or Recent
const TOP_OR_RECENT = [
  { value: 'TOP', label: 'Top Posts', description: 'Get targets from top/popular posts' },
  { value: 'RECENT', label: 'Recent Posts', description: 'Get targets from recent posts' },
];

// Default source values
const DEFAULT_SOURCE = {
  enabled: false,
  probability_min: 5,
  probability_max: 10,
  target_type: 'HASHTAGS',
  target_list: '',
  poster_or_post_engager: 'POSTENGAGER',
  top_or_recent: 'TOP',
  comment_spin_texts_list: '',
  mute_posts_after_follow: false,
  mute_stories_after_follow: false,
};


/* Aurora (2026-08-23): glass cards, semantic tokens, mobile-first grids. The section label
 * style and the inner-panel surface are shared by every block on the page. */
const SECTION_LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
const PANEL = 'rounded-xl bg-secondary/60';

// Source Editor Component
function SourceEditor({ sourceNum, source, onChange, isReadOnly = false }) {
  const isPrimary = sourceNum === 1;

  const handleChange = (field, value) => {
    onChange({ ...source, [field]: value });
  };

  const getTargetCount = () => {
    if (!source.target_list) return 0;
    return source.target_list.split('\n').filter(t => t.trim()).length;
  };

  const TypeIcon = TARGET_TYPES.find(t => t.value === source.target_type)?.icon || Hash;

  return (
    <div className="space-y-6">
      {/* Enable toggle for non-primary sources */}
      {!isPrimary && (
        <div className={cn(PANEL, 'flex flex-col gap-3 border border-border p-4 sm:flex-row sm:items-center sm:justify-between')}>
          <div className="flex items-center gap-3">
            <Switch
              id={`enable-source-${sourceNum}`}
              checked={source.enabled}
              onCheckedChange={(checked) => handleChange('enabled', checked)}
              disabled={isReadOnly}
              data-testid={`enable-source-${sourceNum}-switch`}
            />
            <Label htmlFor={`enable-source-${sourceNum}`} className="font-medium">
              Enable Source {sourceNum}
            </Label>
          </div>

          {source.enabled && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Label className="text-sm text-muted-foreground">Probability:</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={source.probability_min}
                  onChange={(e) => handleChange('probability_min', parseInt(e.target.value) || 0)}
                  className="w-16 h-8 tnum"
                  disabled={isReadOnly}
                  data-testid={`source-${sourceNum}-prob-min`}
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={source.probability_max}
                  onChange={(e) => handleChange('probability_max', parseInt(e.target.value) || 0)}
                  className="w-16 h-8 tnum"
                  disabled={isReadOnly}
                  data-testid={`source-${sourceNum}-prob-max`}
                />
                <span className="text-muted-foreground">%</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main content - only show if enabled (or primary) */}
      {(isPrimary || source.enabled) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left Column - Target Configuration */}
          <div className="min-w-0 space-y-4">
            {/* Target Type */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <TypeIcon className="h-4 w-4 text-primary" />
                Target Type
              </Label>
              <Select
                value={source.target_type}
                onValueChange={(value) => handleChange('target_type', value)}
                disabled={isReadOnly}
              >
                <SelectTrigger data-testid={`source-${sourceNum}-target-type`}>
                  <SelectValue placeholder="Select target type" />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-foreground">
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {TARGET_TYPES.find(t => t.value === source.target_type)?.description}
              </p>
            </div>

            {/* Selection Type - Not shown for DIRECTACCOUNTS */}
            {source.target_type !== 'DIRECTACCOUNTS' && (
              <div className="space-y-2">
                <Label>Targets Selection</Label>
                <Select
                  value={source.poster_or_post_engager}
                  onValueChange={(value) => handleChange('poster_or_post_engager', value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger data-testid={`source-${sourceNum}-selection-type`}>
                    <SelectValue placeholder="Select targets" />
                  </SelectTrigger>
                  <SelectContent>
                    {SELECTION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-foreground">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {SELECTION_TYPES.find(t => t.value === source.poster_or_post_engager)?.description}
                </p>
              </div>
            )}

            {/* Top or Recent - Only for HASHTAGS and LOCATIONS */}
            {(source.target_type === 'HASHTAGS' || source.target_type === 'LOCATIONS') && (
              <div className="space-y-2">
                <Label>Post Type</Label>
                <Select
                  value={source.top_or_recent}
                  onValueChange={(value) => handleChange('top_or_recent', value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger data-testid={`source-${sourceNum}-top-or-recent`}>
                    <SelectValue placeholder="Select post type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TOP_OR_RECENT.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-foreground">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Post-Follow Actions */}
            {!isPrimary && (
              <div className={cn(PANEL, 'space-y-3 p-4')}>
                <Label className={SECTION_LABEL}>Post-Follow Actions</Label>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor={`mute-posts-${sourceNum}`} className="text-sm">Mute Posts After Follow</Label>
                  <Switch
                    id={`mute-posts-${sourceNum}`}
                    checked={source.mute_posts_after_follow}
                    onCheckedChange={(checked) => handleChange('mute_posts_after_follow', checked)}
                    disabled={isReadOnly}
                    data-testid={`source-${sourceNum}-mute-posts`}
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor={`mute-stories-${sourceNum}`} className="text-sm">Mute Stories After Follow</Label>
                  <Switch
                    id={`mute-stories-${sourceNum}`}
                    checked={source.mute_stories_after_follow}
                    onCheckedChange={(checked) => handleChange('mute_stories_after_follow', checked)}
                    disabled={isReadOnly}
                    data-testid={`source-${sourceNum}-mute-stories`}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Target List & Comments (the big text areas stay full width) */}
          <div className="min-w-0 space-y-4">
            {/* Target List */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="min-w-0">
                  {source.target_type === 'HASHTAGS' ? 'Hashtags' :
                   source.target_type === 'LOCATIONS' ? 'Locations' :
                   source.target_type === 'COMPETITORACCOUNTS' ? 'Competitor Accounts' :
                   'Target Usernames'} (one per line)
                </Label>
                <Badge variant="secondary" className="shrink-0 tnum">
                  {getTargetCount()} targets
                </Badge>
              </div>
              <Textarea
                placeholder={
                  source.target_type === 'HASHTAGS' ? '#photography\n#travel\n#food' :
                  source.target_type === 'LOCATIONS' ? 'New York\nLos Angeles\nMiami' :
                  source.target_type === 'COMPETITORACCOUNTS' ? '@competitor1\n@competitor2' :
                  'username1\nusername2\nusername3'
                }
                value={source.target_list}
                onChange={(e) => handleChange('target_list', e.target.value)}
                className="min-h-[200px] w-full font-mono text-sm"
                disabled={isReadOnly}
                data-testid={`source-${sourceNum}-target-list`}
              />
            </div>

            {/* Comment Spintax */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Comment Spintax (one per line)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      type="button"
                      aria-label="How spintax works"
                      className="tap inline-flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <Info className="h-4 w-4" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Use spintax for variation: {'{Nice|Cool|Great}'} {'{post|photo|image}'}!</p>
                      <p className="mt-1">This creates: "Nice post!", "Cool photo!", etc.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Textarea
                placeholder="{Nice|Cool|Great} {post|photo|image}!
{Love|Adore} this {content|stuff}! 🔥
{Amazing|Awesome} work! 👏"
                value={source.comment_spin_texts_list}
                onChange={(e) => handleChange('comment_spin_texts_list', e.target.value)}
                className="min-h-[120px] w-full font-mono text-sm"
                disabled={isReadOnly}
                data-testid={`source-${sourceNum}-comments`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* A grey block while the first load is in flight. */
function Bone({ className }) {
  return <div className={cn('animate-pulse rounded-xl bg-secondary/60', className)} aria-hidden />;
}

function TargetDataSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-4" aria-busy="true" aria-label="Loading target data" data-testid="target-data-skeleton">
      <div className="min-w-0 space-y-6 xl:col-span-3">
        <div className="aurora-card p-5 sm:p-6">
          <Bone className="h-4 w-40" />
          <Bone className="mt-2 h-3 w-64 max-w-full" />
          <Bone className="mt-5 h-9 w-full" />
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i}>
                  <Bone className="h-3 w-24" />
                  <Bone className="mt-2 h-9 w-full" />
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <Bone className="h-3 w-32" />
              <Bone className="h-[200px] w-full" />
              <Bone className="h-3 w-40" />
              <Bone className="h-[120px] w-full" />
            </div>
          </div>
        </div>
        {[0, 1].map((i) => (
          <div key={i} className="aurora-card p-5 sm:p-6">
            <Bone className="h-4 w-40" />
            <Bone className="mt-2 h-3 w-56 max-w-full" />
            <Bone className="mt-5 h-14 w-full" />
            <Bone className="mt-3 h-14 w-full" />
          </div>
        ))}
      </div>
      <div className="min-w-0 space-y-6">
        {[0, 1].map((i) => (
          <div key={i} className="aurora-card p-5 sm:p-6">
            <Bone className="h-4 w-28" />
            {[0, 1, 2, 3].map((j) => <Bone key={j} className="mt-4 h-4 w-full" />)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Main Page Component
export default function SlotTargetDataPage() {
  const { slotId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSource, setActiveSource] = useState('1');
  usePageTitle('Target Data');

  /*
   * Round 14 (lib/r14Switch): the settings the SERVER defines for this page on top of the 50 fields
   * ("Pick targets in order (one per session)", "Auto-remove not-found targets from the list" -
   * slot settings 126 / 127 until they moved here), and the "Targets not found (skipped)" list with
   * Restore. OFF = the round-13 page: no metadata request, no extra switches, no list.
   * Every label and tooltip below comes from GET /uiapi/target-data/metadata; none is written here.
   */
  const r14 = r14UiEnabled();
  const [serverValues, setServerValues] = useState({});
  const { data: targetMeta, error: targetMetaError } = useQuery({
    queryKey: ['targetDataMeta'],
    queryFn: () => targetDataApi.metadata().then(r => r.data),
    enabled: r14,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const serverSettings = React.useMemo(
    () => (r14 ? serverTargetSettings(targetMeta) : []),
    [r14, targetMeta],
  );
  useEffect(() => {
    if (!r14) return;
    if (targetMetaError) {
      const st = targetMetaError?.response?.status;
      // eslint-disable-next-line no-console
      console.info(`[R14-UI] target data: no server-defined settings (GET /target-data/metadata -> ${st || targetMetaError.message}); ${st === 404 ? 'a server without them (round 13)' : 'none drawn'}`);
    } else if (targetMeta) {
      // eslint-disable-next-line no-console
      console.info(`[R14-UI] target data: server-defined settings [${serverSettings.map((x) => x.id).join(', ')}]`
        + (Array.isArray(targetMeta.hidden) && targetMeta.hidden.length ? `, switched off on the server [${targetMeta.hidden.join(', ')}]` : ''));
    }
  }, [r14, targetMeta, targetMetaError, serverSettings]);

  // Form state
  const [formData, setFormData] = useState({
    // Primary source
    target_type: 'HASHTAGS',
    target_list: '',
    poster_or_post_engager: 'POSTENGAGER',
    top_or_recent: 'TOP',
    comment_spin_texts_list: '',

    // Behavior
    dont_remove_targets_after_using_them: false,
    remove_comment_line_after_using_them: false,

    // Follow-back ratio
    enable_removing_bad_fb_ratio_targets: false,
    remove_targets_having_fb_ratio_lower_than_this: 5,
    start_this_removal_after_atleast_this_many_follows_are_done: 500,

    // Sources 2-5
    enable_target_source_2_settings: false,
    target_source_2_prob_min: 5,
    target_source_2_prob_max: 10,
    target_type_2: 'HASHTAGS',
    target_list_2: '',
    poster_or_post_engager_2: 'POSTENGAGER',
    top_or_recent_2: 'TOP',
    comment_spin_texts_list_2: '',
    mute_posts_after_follow_2: false,
    mute_stories_after_follow_2: false,

    enable_target_source_3_settings: false,
    target_source_3_prob_min: 5,
    target_source_3_prob_max: 10,
    target_type_3: 'HASHTAGS',
    target_list_3: '',
    poster_or_post_engager_3: 'POSTENGAGER',
    top_or_recent_3: 'TOP',
    comment_spin_texts_list_3: '',
    mute_posts_after_follow_3: false,
    mute_stories_after_follow_3: false,

    enable_target_source_4_settings: false,
    target_source_4_prob_min: 5,
    target_source_4_prob_max: 10,
    target_type_4: 'HASHTAGS',
    target_list_4: '',
    poster_or_post_engager_4: 'POSTENGAGER',
    top_or_recent_4: 'TOP',
    comment_spin_texts_list_4: '',
    mute_posts_after_follow_4: false,
    mute_stories_after_follow_4: false,

    enable_target_source_5_settings: false,
    target_source_5_prob_min: 5,
    target_source_5_prob_max: 10,
    target_type_5: 'HASHTAGS',
    target_list_5: '',
    poster_or_post_engager_5: 'POSTENGAGER',
    top_or_recent_5: 'TOP',
    comment_spin_texts_list_5: '',
    mute_posts_after_follow_5: false,
    mute_stories_after_follow_5: false,
  });

  /*
   * One query, through the shared client. Three things went with the rewrite:
   *   - the slot-details call. It only ever supplied a display name, and `slotId` IS the slot name.
   *   - the target-stats call. There is no follow-back-ratio data on the server to serve; the
   *     panel that consumed it was already conditional, so it simply does not render.
   *   - the hand-rolled Bearer header. The shared client carries the session and the CSRF token.
   */
  const { data: targetData, isLoading, isError, error, isFetching: targetFetching, refetch: refetchTargetData } = useQuery({
    queryKey: ['targetData', slotId],
    queryFn: () => targetDataApi.get(slotId).then(r => r.data),
  });

  // Merge server values into the form. See lib/targetData.js - a blind spread put slot_name into
  // the body (which the server refuses) and let a null fall back to a default the operator never
  // chose. Both are tested there.
  /*
   * Seed ONCE. react-query keeps `targetData`'s identity while the response is deep-equal and gives
   * it a NEW identity exactly when the server's values have changed - so re-seeding on every change
   * would fire precisely when it would overwrite the user with different values. Same failure the
   * device settings page had, and the same guard.
   *
   * `seeded` rather than a `formData === null` test because formData starts as a populated object
   * of defaults here, not null.
   */
  const seeded = React.useRef(false);
  // The server-defined settings seed on their own: the metadata can arrive after the data. Same
  // once-only rule; every place that re-arms `seeded` re-arms this too.
  const seededServer = React.useRef(false);
  useEffect(() => { seeded.current = false; seededServer.current = false; setServerValues({}); }, [slotId]);
  useEffect(() => {
    if (targetData && !seeded.current) {
      seeded.current = true;
      setFormData(prev => mergeTargetData(prev, targetData));
    }
  }, [targetData]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (targetData && serverSettings.length && !seededServer.current) {
      seededServer.current = true;
      setServerValues(seedServerValues(serverSettings, targetData));
    }
  }, [targetData, serverSettings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => targetDataApi.save(slotId, data),
    onSuccess: (res) => {
      // Re-seed from the server after a save: it normalises some fields on write, and the form
      // should show what was actually STORED rather than what was typed.
      seeded.current = false;
      seededServer.current = false;
      queryClient.invalidateQueries({ queryKey: ['targetData', slotId] });
      // Round 14: the not-found card on this page says whether auto-remove is ON - re-read it, or it
      // keeps showing the value from before this save.
      if (r14) queryClient.invalidateQueries({ queryKey: ['notFoundTargets', slotId] });
      setHasChanges(false);
      const n = res?.data?.changed;
      toast.success(n ? `${n} field${n === 1 ? '' : 's'} saved` : 'Target data saved');
    },
    onError: (err) => {
      const d = err?.response?.data;
      toast.error(d?.message || d?.error || err?.message || 'Failed to save target data');
    },
  });

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Round 14: a server-defined setting (keyed by the server's wire name).
  const handleServerSetting = (wire, checked) => {
    setServerValues(prev => ({ ...prev, [wire]: checked === true }));
    setHasChanges(true);
  };
  // Drawn only when the server both lists the setting and returned its value.
  const shownServerSettings = serverSettings.filter(
    (s) => Object.prototype.hasOwnProperty.call(serverValues, s.wire),
  );

  // Handle source changes
  const handleSourceChange = (sourceNum, sourceData) => {
    if (sourceNum === 1) {
      setFormData(prev => ({
        ...prev,
        target_type: sourceData.target_type,
        target_list: sourceData.target_list,
        poster_or_post_engager: sourceData.poster_or_post_engager,
        top_or_recent: sourceData.top_or_recent,
        comment_spin_texts_list: sourceData.comment_spin_texts_list,
      }));
    } else {
      const suffix = `_${sourceNum}`;
      setFormData(prev => ({
        ...prev,
        [`enable_target_source_${sourceNum}_settings`]: sourceData.enabled,
        [`target_source_${sourceNum}_prob_min`]: sourceData.probability_min,
        [`target_source_${sourceNum}_prob_max`]: sourceData.probability_max,
        [`target_type${suffix}`]: sourceData.target_type,
        [`target_list${suffix}`]: sourceData.target_list,
        [`poster_or_post_engager${suffix}`]: sourceData.poster_or_post_engager,
        [`top_or_recent${suffix}`]: sourceData.top_or_recent,
        [`comment_spin_texts_list${suffix}`]: sourceData.comment_spin_texts_list,
        [`mute_posts_after_follow${suffix}`]: sourceData.mute_posts_after_follow,
        [`mute_stories_after_follow${suffix}`]: sourceData.mute_stories_after_follow,
      }));
    }
    setHasChanges(true);
  };

  // Get source data for editor
  const getSourceData = (sourceNum) => {
    if (sourceNum === 1) {
      return {
        enabled: true,
        probability_min: 100,
        probability_max: 100,
        target_type: formData.target_type,
        target_list: formData.target_list,
        poster_or_post_engager: formData.poster_or_post_engager,
        top_or_recent: formData.top_or_recent,
        comment_spin_texts_list: formData.comment_spin_texts_list,
        mute_posts_after_follow: false,
        mute_stories_after_follow: false,
      };
    }

    const suffix = `_${sourceNum}`;
    return {
      enabled: formData[`enable_target_source_${sourceNum}_settings`] || false,
      probability_min: formData[`target_source_${sourceNum}_prob_min`] || 5,
      probability_max: formData[`target_source_${sourceNum}_prob_max`] || 10,
      target_type: formData[`target_type${suffix}`] || 'HASHTAGS',
      target_list: formData[`target_list${suffix}`] || '',
      poster_or_post_engager: formData[`poster_or_post_engager${suffix}`] || 'POSTENGAGER',
      top_or_recent: formData[`top_or_recent${suffix}`] || 'TOP',
      comment_spin_texts_list: formData[`comment_spin_texts_list${suffix}`] || '',
      mute_posts_after_follow: formData[`mute_posts_after_follow${suffix}`] || false,
      mute_stories_after_follow: formData[`mute_stories_after_follow${suffix}`] || false,
    };
  };

  // Handle save
  const handleSave = () => {
    saveMutation.mutate(toTargetDataBody(formData, r14 ? serverValues : undefined));
  };

  // Calculate total targets
  const getTotalTargets = () => {
    let total = 0;
    for (let i = 1; i <= 5; i++) {
      const source = getSourceData(i);
      if (source.enabled && source.target_list) {
        total += source.target_list.split('\n').filter(t => t.trim()).length;
      }
    }
    return total;
  };

  // Get enabled sources count
  const getEnabledSourcesCount = () => {
    let count = 1; // Primary is always enabled
    for (let i = 2; i <= 5; i++) {
      if (formData[`enable_target_source_${i}_settings`]) count++;
    }
    return count;
  };

  /* The header is shared by the loading and the loaded render so the back / refresh / save
   * controls never jump. The icon back button stays next to PageHeader's own "← Slots" link
   * because its test id and navigate() handler are part of the page's contract. */
  const header = (
    <PageHeader
      icon={Target}
      title="Target Data"
      description={isLoading
        ? `${slotId} • loading…`
        : `${slotId} • ${getTotalTargets()} total targets • ${getEnabledSourcesCount()} sources`}
      back={{ to: '/slots', label: 'Slots' }}
      actions={(
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/slots')}
            className="tap text-muted-foreground hover:text-foreground"
            aria-label="Back to slots"
            title="Back to slots"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (hasChanges && !window.confirm('Discard your unsaved changes and reload from the server?')) return;
              seeded.current = false;          // an explicit refresh MAY overwrite the form
              seededServer.current = false;
              refetchTargetData();
            }}
            className="tap rounded-full"
            data-testid="refresh-button"
          >
            <RefreshCw className={cn('h-4 w-4', targetFetching && 'animate-spin text-primary')} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            className="tap rounded-full shadow-glow"
            data-testid="save-button"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </>
      )}
    />
  );

  if (isLoading) {
    return (
      <div data-testid="slot-target-data-page">
        {header}
        <TargetDataSkeleton />
      </div>
    );
  }

  /*
   * THE branch whose absence caused this page to lie for its entire life. The load used to be
   * `if (targetData) { setFormData(...) }` with nothing on the else, so a failed fetch simply left
   * the hardcoded initial state rendering as though it were the customer's data - HASHTAGS, an
   * empty target list, POSTENGAGER - identically on every slot of every account.
   *
   * A form must never render defaults it cannot distinguish from real values. If the data did not
   * arrive, say so and show nothing else: there is no editing to do, and saving from here would
   * write those defaults over whatever is actually stored.
   */
  if (isError || !targetData) {
    const detail = error?.response?.data?.message
      || error?.response?.data?.error
      || error?.message
      || 'The server did not return the targeting data for this slot.';
    return (
      <div data-testid="slot-target-data-page">
        {header}
        <Card className="aurora-card !border-danger/30" data-testid="target-data-error">
          <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
            <CardTitle className="font-heading text-base font-semibold text-danger">
              Targeting data could not be loaded
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
            <p className="text-sm text-muted-foreground text-pretty">{detail}</p>
            <p className="text-sm text-muted-foreground text-pretty">
              Nothing is shown rather than defaults, because a default here is indistinguishable
              from a real setting - and saving one would overwrite what {slotId} actually targets.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="tap rounded-full"
                onClick={() => refetchTargetData()} data-testid="error-retry">
                <RefreshCw className={cn('h-4 w-4', targetFetching && 'animate-spin')} />
                Try again
              </Button>
              <Button variant="ghost" size="sm" className="tap rounded-full"
                onClick={() => navigate('/slots')}>
                Back to slots
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="slot-target-data-page">
      {header}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
        {/* Main Content */}
        <div className="min-w-0 space-y-6 xl:col-span-3">
          {/* Source Tabs */}
          <Card className="aurora-card">
            <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
              <CardTitle className="font-heading text-base font-semibold">Target Sources</CardTitle>
              <CardDescription>
                Configure up to 5 target sources with probability-based selection
              </CardDescription>
            </CardHeader>
            <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
              <Tabs value={activeSource} onValueChange={setActiveSource}>
                {/* Five triggers do not fit 375px as a grid; the list scrolls sideways on phones
                    and becomes the even 5-column grid from md up. */}
                <TabsList className="no-scrollbar flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-secondary/60 p-1 md:grid md:grid-cols-5">
                  <TabsTrigger value="1" className="tap relative shrink-0 py-1.5" data-testid="source-1-tab">
                    Primary
                    <Badge variant="secondary" className="ml-1 px-1 text-xs tnum">
                      {(formData.target_list?.split('\n').filter(t => t.trim()).length) || 0}
                    </Badge>
                  </TabsTrigger>
                  {[2, 3, 4, 5].map(num => (
                    <TabsTrigger
                      key={num}
                      value={String(num)}
                      className="tap relative shrink-0 py-1.5"
                      data-testid={`source-${num}-tab`}
                    >
                      Source {num}
                      {formData[`enable_target_source_${num}_settings`] && (
                        <Badge variant="default" className="ml-1 px-1 text-xs bg-success/15 text-success shadow-none hover:bg-success/15">
                          ON
                        </Badge>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {[1, 2, 3, 4, 5].map(num => (
                  <TabsContent key={num} value={String(num)} className="mt-4">
                    <SourceEditor
                      sourceNum={num}
                      source={getSourceData(num)}
                      onChange={(data) => handleSourceChange(num, data)}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Behavior Settings */}
          <Card className="aurora-card">
            <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
              <CardTitle className="font-heading text-base font-semibold">Behavior Settings</CardTitle>
              <CardDescription>
                Configure how targets and comments are handled after use
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
              <div className={cn(PANEL, 'flex items-center justify-between gap-3 p-3 sm:p-4')}>
                <div className="min-w-0">
                  <Label className="font-medium">Don't Remove Targets After Using</Label>
                  <p className="text-sm text-muted-foreground">
                    Keep targets in the list even after they've been engaged
                  </p>
                </div>
                <Switch
                  checked={formData.dont_remove_targets_after_using_them}
                  onCheckedChange={(checked) => handleChange('dont_remove_targets_after_using_them', checked)}
                  data-testid="dont-remove-targets-switch"
                />
              </div>

              <div className={cn(PANEL, 'flex items-center justify-between gap-3 p-3 sm:p-4')}>
                <div className="min-w-0">
                  <Label className="font-medium">Remove Comment After Using</Label>
                  <p className="text-sm text-muted-foreground">
                    Remove comment lines from the list after they've been used
                  </p>
                </div>
                <Switch
                  checked={formData.remove_comment_line_after_using_them}
                  onCheckedChange={(checked) => handleChange('remove_comment_line_after_using_them', checked)}
                  data-testid="remove-comment-switch"
                />
              </div>

              {/* Round 14: the server-defined settings of this page (label + tooltip from the server). */}
              {shownServerSettings.map((s) => (
                <div
                  key={s.wire}
                  className={cn(PANEL, 'flex items-center justify-between gap-3 p-3 sm:p-4')}
                  data-testid={`target-setting-${s.wire}`}
                >
                  <div className="min-w-0">
                    <Label htmlFor={`target-setting-${s.wire}-switch`} className="font-medium">{s.label}</Label>
                    {s.tooltip && (
                      <p className="text-sm text-muted-foreground text-pretty" data-testid={`target-setting-${s.wire}-help`}>
                        {s.tooltip}
                      </p>
                    )}
                  </div>
                  <Switch
                    id={`target-setting-${s.wire}-switch`}
                    checked={serverValues[s.wire] === true}
                    onCheckedChange={(checked) => handleServerSetting(s.wire, checked)}
                    data-testid={`target-setting-${s.wire}-switch`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Round 14: the same "Targets not found (skipped)" list with Restore as on Slot Stats. */}
          {r14 && <NotFoundTargetsCard slotName={slotId} />}

          {/* Follow-Back Ratio Settings */}
          <Card className="aurora-card">
            <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
              <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
                <BarChart3 className="h-5 w-5 text-primary" />
                Follow-Back Ratio Optimization
              </CardTitle>
              <CardDescription>
                Automatically remove low-performing target sources based on follow-back ratio
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
              <div className={cn(PANEL, 'flex items-center justify-between gap-3 p-3 sm:p-4')}>
                <div className="min-w-0">
                  <Label className="font-medium">Enable Follow-Back Ratio Optimization</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically remove sources with low follow-back rates
                  </p>
                </div>
                <Switch
                  checked={formData.enable_removing_bad_fb_ratio_targets}
                  onCheckedChange={(checked) => handleChange('enable_removing_bad_fb_ratio_targets', checked)}
                  data-testid="enable-fb-ratio-switch"
                />
              </div>

              {formData.enable_removing_bad_fb_ratio_targets && (
                <div className="grid grid-cols-1 gap-4 rounded-xl border border-border p-4 md:grid-cols-2">
                  <div className="min-w-0 space-y-2">
                    <Label>Minimum Follow-Back Ratio (%)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={formData.remove_targets_having_fb_ratio_lower_than_this}
                        onChange={(e) => handleChange('remove_targets_having_fb_ratio_lower_than_this', parseInt(e.target.value) || 0)}
                        className="w-24 tnum"
                        data-testid="fb-ratio-min-input"
                      />
                      <span className="text-muted-foreground">%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Sources with ratio below this will be removed
                    </p>
                  </div>

                  <div className="min-w-0 space-y-2">
                    <Label>Start After Follows</Label>
                    <Input
                      type="number"
                      min={0}
                      value={formData.start_this_removal_after_atleast_this_many_follows_are_done}
                      onChange={(e) => handleChange('start_this_removal_after_atleast_this_many_follows_are_done', parseInt(e.target.value) || 0)}
                      className="w-32 tnum"
                      data-testid="fb-ratio-start-after-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Wait until this many follows before evaluating
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Stats */}
        <div className="min-w-0 space-y-6">
          {/* Quick Stats */}
          <Card className="aurora-card">
            <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
              <CardTitle className="font-heading text-base font-semibold">Statistics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-muted-foreground">Total Targets</span>
                  <span className="font-semibold tnum">{getTotalTargets()}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-muted-foreground">Active Sources</span>
                  <span className="font-semibold tnum">{getEnabledSourcesCount()}</span>
                </div>
                <Separator />

                {/* Per-source breakdown */}
                <div className="space-y-2">
                  <span className={cn(SECTION_LABEL, 'block')}>Per Source</span>
                  {[1, 2, 3, 4, 5].map(num => {
                    const source = getSourceData(num);
                    if (!source.enabled) return null;
                    const count = source.target_list?.split('\n').filter(t => t.trim()).length || 0;
                    return (
                      <div key={num} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-muted-foreground">
                          {num === 1 ? 'Primary' : `Source ${num}`}
                        </span>
                        <Badge variant="outline" className="border-border tnum">
                          {count}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* A "Follow-Back Ratios" panel stood here, fed by
              GET /api/slots/:id/target-data/stats - a V2 route this server has never had. There is
              no follow-back-ratio data on Target to serve from either: only the two config fields
              (enable-removal and its threshold), which are edited above. It was dead in every
              render. Removed rather than left as a permanently empty conditional - if the stat is
              ever wanted, it first needs somewhere to be computed FROM. */}

          {/* Help Card */}
          <Card className="aurora-card">
            <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
              <CardTitle className="font-heading text-base font-semibold">Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-5 pt-0 text-sm text-muted-foreground sm:p-6 sm:pt-0">
              <p>• Use multiple sources with probabilities to diversify targeting</p>
              <p>• Spintax in comments: {'{Nice|Cool}'} creates "Nice" or "Cool"</p>
              <p>• DIRECTACCOUNTS removes targets after use, others don't</p>
              <p>• Enable FB ratio optimization after 500+ follows for best results</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
